import React, { useLayoutEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useWrapPoints } from '../../hooks/useWrapPoints';
import { useCrop } from '../../hooks/useCrop';
import { CropShapeGallery } from './CropShapeGallery';
import { cropShapeLabel } from '../../lib/cropShape';
import { WithTip } from '../ribbon/RibbonPrimitives';
import { TooltipProvider } from '../ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { pickLocalFile } from './ObjectContextMenu';
import { FRAME_STYLES, frameStyleProps, frameStyleThumb, currentFrameStyle } from '../../lib/pictureStyles';
import { Replace, Crop, Minimize2, Maximize2, Frame, WrapText, BringToFront, SendToBack, Settings2, ImageOff, Image as ImageIcon, Spline, ChevronDown, Shapes } from 'lucide-react';

const GAP = 42; // px between the picture frame and the toolbar above it (clears the rotation handle, 34px above the frame)
const GAP_BELOW = 12; // px when flipped below the picture
const TOP_MARGIN = 4; // px kept from the page top before flipping below

const WRAP_STYLES = [['none', 'None'], ['square', 'Square'], ['tight', 'Tight'], ['through', 'Through'], ['topBottom', 'Top and Bottom']];

const btn = 'h-7 min-w-7 px-1.5 inline-flex items-center justify-center gap-0.5 rounded-sm text-foreground/85 hover:bg-[hsl(var(--wp-command-hover))] active:bg-[hsl(var(--wp-command-pressed))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none';
const menuItem = 'text-[13px] flex items-center gap-2 cursor-pointer';
const menuClass = 'bg-popover text-popover-foreground border border-border shadow-md min-w-[200px] z-[120]';

/** Axis-aligned bounds (page px) of a possibly rotated object. */
const rotatedBounds = (o) => {
  const w = Math.max(1, o.width), h = Math.max(1, o.height);
  const r = ((o.rotation || 0) * Math.PI) / 180, c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
  const bw = w * c + h * s, bh = w * s + h * c;
  const cx = o.x + w / 2, cy = o.y + h / 2;
  return { cx, cy, top: cy - bh / 2, bottom: cy + bh / 2 };
};

/**
 * Publisher-style picture mini toolbar: floats above the selected picture (below it when there is no room at the page
 * top), follows the picture as it moves and disappears while it is being dragged / resized / rotated or edited.
 * Rendered inside the page container so it scrolls with the page; sized in screen pixels (does not zoom).
 */
export const PictureMiniToolbar = ({ object, zoom }) => {
  const { state, dispatch, openDialog } = useEditor();
  const crop = useCrop();
  const wrapPoints = useWrapPoints();
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 34 });
  const p = object.properties || {};
  const id = object.id;
  const set = (props) => dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [id], props } });

  const hidden = state.interacting === id || state.showPhotoEditor || state.showImageGallery || state.editingObjectId === id;

  // Measure the toolbar (screen px) so it can be centred / flipped; re-attach whenever it re-appears.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setSize((s) => (Math.abs(width - s.w) > 0.5 || Math.abs(height - s.h) > 0.5 ? { w: width, h: height } : s));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hidden]);

  if (hidden) return null;

  const b = rotatedBounds(object);
  const pageW = (state.pages[state.currentPageIndex]?.width || 816) * zoom;
  const above = b.top * zoom - GAP - size.h;
  const flip = above < TOP_MARGIN;
  const top = flip ? b.bottom * zoom + GAP_BELOW : above;
  const left = Math.max(0, Math.min(pageW - size.w, b.cx * zoom - size.w / 2));
  const fit = p.objectFit || 'cover';
  const styleId = currentFrameStyle(p);

  return (
    <TooltipProvider delayDuration={500}>
    <div
      ref={ref}
      className="absolute flex items-center gap-px rounded-md border border-border bg-popover/95 backdrop-blur-md p-1 shadow-lg text-popover-foreground"
      style={{ left, top, zIndex: 1001, opacity: size.w ? 1 : 0 }}
      role="toolbar"
      aria-label="Picture tools"
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      data-testid="selection-mini-toolbar"
      data-kind="picture"
      data-placement={flip ? 'below' : 'above'}
    >
      {/* Change Picture */}
      <DropdownMenu modal={false}>
        <WithTip tip="Change Picture">
          <DropdownMenuTrigger asChild>
            <button type="button" className={btn} aria-label="Change Picture" data-testid="mini-toolbar-change-picture"><Replace className="h-4 w-4" /><ChevronDown className="h-3 w-3 opacity-60" /></button>
          </DropdownMenuTrigger>
        </WithTip>
        <DropdownMenuContent align="start" className={menuClass} data-testid="mini-toolbar-change-picture-menu">
          <DropdownMenuItem className={menuItem} onClick={() => pickLocalFile((src) => set({ src, placeholder: false }))} data-testid="mini-toolbar-menu-change-file"><ImageIcon className="w-4 h-4 text-muted-foreground" /> From File...</DropdownMenuItem>
          <DropdownMenuItem className={menuItem} onClick={() => dispatch({ type: 'SHOW_IMAGE_GALLERY', payload: { mode: 'replace' } })} data-testid="mini-toolbar-menu-change-online"><Replace className="w-4 h-4 text-muted-foreground" /> From Online Pictures...</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className={menuItem} disabled={!p.src} onClick={() => set({ src: '', placeholder: true })} data-testid="mini-toolbar-menu-remove-picture"><ImageOff className="w-4 h-4 text-muted-foreground" /> Remove Picture (keep frame)</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Crop */}
      <WithTip tip="Crop — black handles appear on the picture; drag them to crop in place">
        <button type="button" className={btn} disabled={!p.src} onClick={() => crop.enter(object.id)} aria-label="Crop" data-testid="mini-toolbar-crop"><Crop className="h-4 w-4" /></button>
      </WithTip>
      {/* Crop to Shape (the gallery popover is the trigger's parent, so the hint is a plain title) */}
      <CropShapeGallery current={p.cropShape} onPick={(id) => crop.setShape(id, object.id)} testId="mini-toolbar-crop-shape-gallery">
        <button type="button" className={`${btn} ${p.cropShape ? 'bg-primary/15 text-primary' : ''}`} aria-pressed={!!p.cropShape} aria-label="Crop to Shape" title={p.cropShape ? `Crop to Shape — currently ${cropShapeLabel(p.cropShape)}` : 'Crop to Shape — circle, star, heart or any closed shape'} data-testid="mini-toolbar-crop-shape"><Shapes className="h-4 w-4" /><ChevronDown className="h-3 w-3 opacity-60" /></button>
      </CropShapeGallery>

      <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />

      {/* Fit / Fill */}
      <WithTip tip="Fit — show the whole picture inside its frame">
        <button type="button" className={`${btn} ${fit === 'contain' ? 'bg-primary/15 text-primary' : ''}`} aria-pressed={fit === 'contain'} onClick={() => crop.fit(object.id)} aria-label="Fit" data-testid="mini-toolbar-fit"><Minimize2 className="h-4 w-4" /></button>
      </WithTip>
      <WithTip tip="Fill — fill the frame, cropping the picture">
        <button type="button" className={`${btn} ${fit === 'cover' ? 'bg-primary/15 text-primary' : ''}`} aria-pressed={fit === 'cover'} onClick={() => crop.fill(object.id)} aria-label="Fill" data-testid="mini-toolbar-fill"><Maximize2 className="h-4 w-4" /></button>
      </WithTip>

      <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />

      {/* Picture Styles */}
      <DropdownMenu modal={false}>
        <WithTip tip="Picture Styles">
          <DropdownMenuTrigger asChild>
            <button type="button" className={btn} aria-label="Picture Styles" data-testid="mini-toolbar-styles"><Frame className="h-4 w-4" /><ChevronDown className="h-3 w-3 opacity-60" /></button>
          </DropdownMenuTrigger>
        </WithTip>
        <DropdownMenuContent align="start" className={`${menuClass} p-2`} data-testid="mini-toolbar-styles-menu">
          <DropdownMenuLabel className="text-[11px] text-muted-foreground px-0 pb-1">Picture Styles</DropdownMenuLabel>
          <div className="grid grid-cols-4 gap-1">
            {FRAME_STYLES.map((f) => (
              <DropdownMenuItem key={f.id} title={f.label} onClick={() => set(frameStyleProps(f))} className={`h-[34px] w-[40px] p-0 justify-center rounded-sm focus:bg-primary/15 ${styleId === f.id ? 'bg-primary/10 ring-1 ring-primary/50' : ''}`} data-testid={`mini-toolbar-style-${f.id}`}>
                <span className="block h-[22px] w-[26px] bg-gradient-to-br from-sky-300 to-emerald-300" style={frameStyleThumb(f)} />
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Wrap Text */}
      <DropdownMenu modal={false}>
        <WithTip tip="Wrap Text">
          <DropdownMenuTrigger asChild>
            <button type="button" className={btn} aria-label="Wrap Text" data-testid="mini-toolbar-wrap"><WrapText className="h-4 w-4" /><ChevronDown className="h-3 w-3 opacity-60" /></button>
          </DropdownMenuTrigger>
        </WithTip>
        <DropdownMenuContent align="start" className={menuClass} data-testid="mini-toolbar-wrap-menu">
          {WRAP_STYLES.map(([m, l]) => (
            <DropdownMenuItem key={m} className={`${menuItem} ${(p.wrapStyle || 'none') === m ? 'bg-primary/10' : ''}`} onClick={() => set({ wrapStyle: m })} data-testid={`mini-toolbar-wrap-${m}`}>{l}</DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className={menuItem} disabled={!wrapPoints.canEdit(object)} onClick={() => wrapPoints.enter(id)} data-testid="mini-toolbar-edit-wrap-points"><Spline className="w-4 h-4 text-muted-foreground" /> Edit Wrap Points</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />

      {/* Order */}
      <WithTip tip="Bring Forward">
        <button type="button" className={btn} onClick={() => dispatch({ type: 'BRING_FORWARD' })} aria-label="Bring Forward" data-testid="mini-toolbar-bring-forward"><BringToFront className="h-4 w-4" /></button>
      </WithTip>
      <WithTip tip="Send Backward">
        <button type="button" className={btn} onClick={() => dispatch({ type: 'SEND_BACKWARD' })} aria-label="Send Backward" data-testid="mini-toolbar-send-backward"><SendToBack className="h-4 w-4" /></button>
      </WithTip>

      <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />

      {/* Format Picture… */}
      <WithTip tip="Format Picture…">
        <button type="button" className={btn} onClick={() => openDialog('format-object', { objectId: id, tab: 'picture' })} aria-label="Format Picture" data-testid="mini-toolbar-format"><Settings2 className="h-4 w-4" /></button>
      </WithTip>
    </div>
    </TooltipProvider>
  );
};
